import { useApiErrorSnackbar } from '../common/useApiErrorSnackbar';
import useWalletsApi from '~/composables/api/useWalletsApi';
import { walletStatuses } from '~/constants';
import { useSupportWindowUtils } from '@/utils/supportWindowUtils';
import { useWalletsStore } from '~/stores/wallets/wallets-store';
import type {
  DropdownButtonType,
  LogsStatusType,
  SnackbarMessage,
  StatusWalletType
} from '~/types';

export const useWalletActions = () => {
  const { $i18n } = useNuxtApp();
  const { t, locale } = $i18n;
  const route = useRoute();
  const { add } = useSnackbar();
  const { notifyApiError } = useApiErrorSnackbar();
  const { putBlockUnblockWallet } = useWalletsApi();
  const { openSupportWindowHandler } = useSupportWindowUtils();
  const runtimeConfig = useConfig();
  const {
    userInfos,
    openBlockUnblockWalletModal,
    openEditWalletStatusModel,
    openEditBankAccountStatusModel,
    openPostIdentLinkModal,
    generateKycLinkStep
  } = storeToRefs(useWalletsStore());

  const walletIntIdParam = computed(() =>
    Array.isArray(route.params.id)
      ? parseInt(route.params.id[0], 10)
      : parseInt(route.params.id, 10)
  );

  const blockUnblockBody = ref({
    Comment: { value: undefined as string | undefined, isValid: undefined as boolean | undefined },
    Password: { value: undefined as string | undefined, isValid: undefined as boolean | undefined }
  });

  const walletStatusBody = ref<StatusWalletType>({
    value: undefined,
    isValid: undefined
  });

  const generatePostIdentLinkSteps = ref(0);

  const blockUnblockPermission = computed(
    () =>
      (userInfos.value.blockedByPartner && checkPermission('doWalletUnFreezeReasonPartner')) ||
      (!userInfos.value.blockedByPartner && checkPermission('doWalletFreezeReasonPartner'))
  );

  const autoKycPermission = computed(
    () =>
      checkPermission('postIdent') &&
      !userInfos.value.blockedByPartner &&
      userInfos.value.situationId !== 6
  );

  const walletActions = computed<DropdownButtonType[]>(() => [
    {
      text: t('wallets.details.editWalletStatus'),
      iconPosition: 'left',
      leftIcon: 'Edit',
      action: () => handleOpenEditWalletStatusModel(),
      theme: 'warning',
      hasBorder: false,
      isDisabled: runtimeConfig.value.public.lemonwayEnv.toLowerCase() === 'production',
      permission: runtimeConfig.value.public.lemonwayEnv.toLowerCase() !== 'production'
    },
    {
      text: t(
        `wallets.details.blockUnblock.${userInfos.value.blockedByPartner ? 'unblockWallet' : 'blockWallet'}`
      ),
      iconPosition: 'left',
      leftIcon: userInfos.value.blockedByPartner ? 'Unlock' : 'Lock',
      action: () => handleOpenBlockUnblockWalletModal(),
      isDisabled: !blockUnblockPermission.value,
      theme: 'ghost',
      permission: blockUnblockPermission.value
    },
    {
      text: t('wallets.details.newSupportTicket'),
      iconPosition: 'left',
      leftIcon: 'Support',
      action: () => {
        const supportSubject = `[KYC]-${userInfos.value.intId}`;
        const supportCategory = 'kyc__know_your_customer_';
        const supportExternalId = userInfos.value?.wallet ?? '';
        openSupportWindowHandler(
          supportSubject,
          supportCategory,
          supportExternalId,
          userInfos.value.intId
        );
      },
      theme: 'ghost',
      permission: true
    },
    {
      text: t('wallets.details.generateKyc.generatePostIdentLinkButton'),
      iconPosition: 'left',
      leftIcon: 'HowToReg',
      action: () => handleGenerateKycLinkModal(),
      theme: 'ghost',
      permission: autoKycPermission.value
    }
  ]);

  const resetBlockUnblockInputs = () => {
    blockUnblockBody.value = {
      Comment: { value: undefined, isValid: undefined },
      Password: { value: undefined, isValid: undefined }
    };
  };

  const handleOpenBlockUnblockWalletModal = () => {
    openBlockUnblockWalletModal.value = !openBlockUnblockWalletModal.value;
    if (!openBlockUnblockWalletModal.value) {
      resetBlockUnblockInputs();
    }
  };

  const handleGenerateKycLinkModal = () => {
    openPostIdentLinkModal.value = !openPostIdentLinkModal.value;
  };

  const handleWalletStatus = (status: number): any => {
    const foundStatus = walletStatuses.find(
      (statusObject: LogsStatusType) => statusObject.value === status
    );
    if (!foundStatus) return {};
    return {
      ...foundStatus,
      text: t(`wallets.status.${foundStatus.text}`),
      substatuses: !t(`wallets.status.${foundStatus.text}`)
        ? t(`wallets.subStatuses.${foundStatus.text}`)
          ? t(`wallets.subStatuses.${foundStatus.text}`).split(', ')
          : undefined
        : undefined
    };
  };

  const handleOpenEditWalletStatusModel = () => {
    openEditWalletStatusModel.value = !openEditWalletStatusModel.value;
    if (!openBlockUnblockWalletModal.value) {
      const walletStatus = computed(() => handleWalletStatus(userInfos.value.situationId));
      walletStatusBody.value = { value: walletStatus.value.value, isValid: true };
    }
  };

  const toggleEditWalletStatusModel = () => {
    openEditWalletStatusModel.value = !openEditWalletStatusModel.value;
  };

  const handleOpenEditBankAccountStatusModel = () => {
    openEditBankAccountStatusModel.value = !openEditBankAccountStatusModel.value;
  };

  const handleBlockUnblockWallet = async () => {
    if (!blockUnblockBody.value.Password.value) return;
    try {
      const formattedBody = {
        Comment: blockUnblockBody.value.Comment.value,
        IsBlocked: !userInfos.value.blockedByPartner,
        Language: locale.value,
        Password: blockUnblockBody.value.Password.value
      };
      await putBlockUnblockWallet(walletIntIdParam.value, formattedBody);
      add({
        type: 'success',
        message: t(
          `wallets.details.blockUnblock.${userInfos.value.blockedByPartner ? 'successUnblock' : 'successBlock'}`,
          { id: walletIntIdParam.value }
        )
      } as SnackbarMessage);
      resetBlockUnblockInputs();
      handleOpenBlockUnblockWalletModal();
    } catch (error: unknown) {
      notifyApiError(error);
    }
  };

  return {
    blockUnblockPermission,
    walletActions,
    blockUnblockBody,
    walletStatusBody,
    generatePostIdentLinkSteps,
    openPostIdentLinkModal,
    generateKycLinkStep,
    handleOpenBlockUnblockWalletModal,
    handleGenerateKycLinkModal,
    handleWalletStatus,
    handleOpenEditWalletStatusModel,
    toggleEditWalletStatusModel,
    handleOpenEditBankAccountStatusModel,
    handleBlockUnblockWallet
  };
};
